import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { RefObject } from 'react'

import { useGuideTour } from '../provider/guideTourContext'
import { hasGuideTourStepTarget } from '../utils/targets'
import { buildGuideTourSkippedStepNoticeMessage } from '../utils/tourRuntime'
import type { GuideTourStep } from '../types'
import {
  GUIDE_TOUR_STEP_CARD_WIDTH,
  getScreenStepCardPosition,
  getStepCardPosition,
} from './utils'

export type GuideTourStepCardModel = {
  cardRef: RefObject<HTMLDivElement>
  top: number
  left: number
  isInModalLayer: boolean
  isSummaryOnly: boolean
  currentStep: GuideTourStep
  currentStepNumber: number
  stepCount: number
  isFirstStep: boolean
  continueLabel: string
  routeTourQueue: { position: number; total: number } | null
  skippedStepNoticeMessage: string | null
  isBlockedTargetInteractionWarningVisible: boolean
  blockedTargetInteractionFeedbackKey: number | undefined
  stepImageUrl: string | null
  handleSkip: () => void
  handleNext: () => void
  handlePrev: () => void
}

const useGuideTourStepCardModel = (): GuideTourStepCardModel | null => {
  const {
    state,
    currentStep,
    currentStepNumber,
    stepCount,
    isFirstStep,
    shouldCompleteOnContinue,
    next,
    prev,
    skip,
    markStepViewed,
    targetBounds,
    isCurrentStepReady,
    isInModalLayer,
    getRouteTourQueue,
    currentPathname,
    blockedTargetInteractionFeedbackKey,
    isBlockedTargetInteractionWarningVisible,
    clearBlockedTargetInteractionWarning,
    skippedStepNotice,
    isSkippedStepNoticeVisible,
    isSkippedStepsSummaryActive,
    clearSkippedStepNotice,
    completeSkippedStepsSummary,
  } = useGuideTour()
  const cardRef = useRef<HTMLDivElement>(null)
  const lastViewedStepIndexRef = useRef(-1)
  const [cardHeight, setCardHeight] = useState(0)
  const isActive = state.status === 'active'
  const hasTarget = hasGuideTourStepTarget(currentStep)

  useLayoutEffect(() => {
    const card = cardRef.current

    if (!card) {
      return undefined
    }

    const updateCardHeight = () => {
      setCardHeight(card.getBoundingClientRect().height)
    }

    updateCardHeight()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateCardHeight)
        : null

    resizeObserver?.observe(card)

    return () => {
      resizeObserver?.disconnect()
    }
  }, [
    currentStep,
    isActive,
    targetBounds,
    hasTarget,
    isBlockedTargetInteractionWarningVisible,
    isSkippedStepNoticeVisible,
    isSkippedStepsSummaryActive,
  ])

  useEffect(() => {
    if (!isActive || !currentStep) {
      return
    }

    const isSummaryOnly = isSkippedStepsSummaryActive

    if (!isSummaryOnly && !isCurrentStepReady) {
      return
    }

    if (hasTarget && !targetBounds) {
      return
    }

    if (lastViewedStepIndexRef.current === state.currentStepIndex) {
      return
    }

    lastViewedStepIndexRef.current = state.currentStepIndex
    markStepViewed()
  }, [
    currentStep,
    hasTarget,
    isActive,
    isCurrentStepReady,
    isSkippedStepsSummaryActive,
    markStepViewed,
    state.currentStepIndex,
    targetBounds,
  ])

  if (!isActive || !currentStep) {
    return null
  }

  const isSummaryOnly = isSkippedStepsSummaryActive

  if (!isSummaryOnly && !isCurrentStepReady) {
    return null
  }

  if (!isSummaryOnly && hasTarget && !targetBounds) {
    return null
  }

  const skippedStepNoticeMessage =
    isSkippedStepNoticeVisible && skippedStepNotice
      ? buildGuideTourSkippedStepNoticeMessage(skippedStepNotice)
      : null

  const cardWidth = Math.min(GUIDE_TOUR_STEP_CARD_WIDTH, window.innerWidth - 32)
  const { top, left } =
    isSummaryOnly || !hasTarget || !targetBounds
      ? getScreenStepCardPosition({
          cardWidth,
          cardHeight: cardHeight || 280,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        })
      : getStepCardPosition({
          bounds: targetBounds,
          placement: currentStep.placement ?? 'auto',
          cardWidth,
          cardHeight: cardHeight || 280,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        })

  const continueLabel =
    isSummaryOnly || shouldCompleteOnContinue ? 'Concluir' : 'Próximo'
  const routeTourQueue =
    state.activeTourId !== null
      ? getRouteTourQueue(currentPathname, state.activeTourId)
      : null

  const handleSkip = () => {
    clearBlockedTargetInteractionWarning()
    clearSkippedStepNotice()
    skip()
  }

  const handleNext = () => {
    clearBlockedTargetInteractionWarning()
    clearSkippedStepNotice()

    if (isSummaryOnly) {
      completeSkippedStepsSummary()
      return
    }

    next()
  }

  const handlePrev = () => {
    clearBlockedTargetInteractionWarning()
    prev()
  }

  const stepImageUrl =
    !isSummaryOnly && currentStep.imageUrl ? currentStep.imageUrl : null

  return {
    cardRef,
    top,
    left,
    isInModalLayer,
    isSummaryOnly,
    currentStep,
    currentStepNumber,
    stepCount,
    isFirstStep,
    continueLabel,
    routeTourQueue,
    skippedStepNoticeMessage,
    isBlockedTargetInteractionWarningVisible,
    blockedTargetInteractionFeedbackKey,
    stepImageUrl,
    handleSkip,
    handleNext,
    handlePrev,
  }
}

export { useGuideTourStepCardModel }
